import { Component, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { classifyError } from "@/lib/errorClassifier";

interface InnerProps {
  children: ReactNode;
  onError: (err: Error) => void;
}

class Inner extends Component<InnerProps, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    try {
      this.props.onError(error);
    } catch (_) {
      /* never let error handler crash */
    }
  }
  render() {
    // Render nothing while we toast + navigate away; previous screen will reappear.
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Lightweight, non-blocking error boundary for RecipeDetail.
 * - Shows a 5s toast with a "Contact support" action.
 * - Invalidates the recipe query cache.
 * - Auto-navigates back after 3s.
 */
export default function RecipeDetailErrorBoundary({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();

  const handleError = (err: Error) => {
    const { category } = classifyError(err);
    const rawCode = err?.message || category || "RECIPE_ERR";
    const code = rawCode.replace(/\s+/g, " ").trim().slice(0, 80);

    // Clear stale recipe queries so a retry refetches fresh data.
    try {
      qc.invalidateQueries({ queryKey: ["recipe"] });
    } catch (_) {}

    const title = t(
      "errors.recipe_detail_toast",
      "Something went wrong. Error: {{code}}",
      { code },
    );

    toast.error(title, {
      icon: <AlertCircle size={18} />,
      duration: 5000,
      action: {
        label: t("errors.contact_support_action", "Contact support"),
        onClick: () => {
          const subject = `Error in recipe: ${code}`;
          const message = `I encountered an error while viewing a recipe. Error code: ${code}. Please help.`;
          navigate(
            `/soporte?prefill_subject=${encodeURIComponent(subject)}&prefill_message=${encodeURIComponent(message)}`,
          );
        },
      },
    });

    // Auto-return to the previous screen after the user has had time to read the toast.
    window.setTimeout(() => {
      try {
        navigate(-1);
      } catch (_) {
        navigate("/recipes");
      }
    }, 3000);
  };

  return <Inner onError={handleError}>{children}</Inner>;
}
